"use client";

import React, { useState, useEffect } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ChevronRight, Loader2, ShieldAlert } from "lucide-react";
function AuditFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border-2 border-primary bg-primary/10 flex flex-col items-center text-center space-y-4 animate-in fade-in duration-500">
                <CheckCircle2 className="size-16 text-primary" />
                <h3 className="text-2xl font-serif text-secondary-foreground uppercase tracking-widest">System Sync Complete</h3>
                <p className="text-secondary-foreground/80 font-mono text-sm max-w-md">
                    {successMessage}
                </p>
                <Button onClick={resetForm} variant="outline" className="mt-6 rounded-none border-primary text-primary hover:bg-[#B5A642] hover:text-primary-foreground font-mono uppercase tracking-wider">
                    Initiate New Scan
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="company_name" rules={{
        required: "Legal Entity Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-primary font-mono text-xs uppercase tracking-widest">Legal Entity Name</FormLabel>
                            <FormControl>
                                <Input placeholder="Enter the legal entity name for the audit." className="rounded-none border-primary/30 bg-black/20 shadow-inner text-secondary-foreground placeholder:text-secondary-foreground/30 focus-visible:ring-primary focus-visible:border-primary h-12 font-mono" {...field} />
                            </FormControl>
                            <FormMessage className="font-mono text-xs text-destructive" />
                        </FormItem>} />

                <FormField control={control} name="contact_name" rules={{
        required: "Primary Decision Maker is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-primary font-mono text-xs uppercase tracking-widest">Primary Decision Maker</FormLabel>
                            <FormControl>
                                <Input placeholder="Primary decision maker for infrastructure." className="rounded-none border-primary/30 bg-black/20 shadow-inner text-secondary-foreground placeholder:text-secondary-foreground/30 focus-visible:ring-primary focus-visible:border-primary h-12 font-mono" {...field} />
                            </FormControl>
                            <FormMessage className="font-mono text-xs text-destructive" />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-primary font-mono text-xs uppercase tracking-widest">Email Address</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="john@example.com" className="rounded-none border-primary/30 bg-black/20 shadow-inner text-secondary-foreground placeholder:text-secondary-foreground/30 focus-visible:ring-primary focus-visible:border-primary h-12 font-mono" {...field} />
                            </FormControl>
                            <FormMessage className="font-mono text-xs text-destructive" />
                        </FormItem>} />

                <FormField control={control} name="phone" rules={{
        required: "Phone Number is required",
        pattern: {
          value: /^\+?[0-9\s\-\(\)]{7,20}$/,
          message: "Invalid phone format"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-primary font-mono text-xs uppercase tracking-widest">Phone Number</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="(555) 123-4567" className="rounded-none border-primary/30 bg-black/20 shadow-inner text-secondary-foreground placeholder:text-secondary-foreground/30 focus-visible:ring-primary focus-visible:border-primary h-12 font-mono" {...field} />
                            </FormControl>
                            <FormMessage className="font-mono text-xs text-destructive" />
                        </FormItem>} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="crm_system" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-primary font-mono text-xs uppercase tracking-widest">Current Operational Stack</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <select className="flex h-12 w-full appearance-none rounded-none border border-primary/30 bg-black/20 px-3 py-1 text-base shadow-inner text-secondary-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 md:text-sm font-mono" {...field}>
                                        <option value="" disabled className="bg-secondary text-secondary-foreground">Identify current operational stack.</option>
                                        <option value="ServiceTitan" className="bg-secondary text-secondary-foreground">ServiceTitan</option>
                                        <option value="Housecall Pro" className="bg-secondary text-secondary-foreground">Housecall Pro</option>
                                        <option value="Jobber" className="bg-secondary text-secondary-foreground">Jobber</option>
                                        <option value="Other" className="bg-secondary text-secondary-foreground">Other</option>
                                    </select>
                                    <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-primary rotate-90 pointer-events-none" />
                                </div>
                            </FormControl>
                            <FormMessage className="font-mono text-xs text-destructive" />
                        </FormItem>} />

                <FormField control={control} name="monthly_call_volume" rules={{
        min: {
          value: 0,
          message: "Volume cannot be negative"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-primary font-mono text-xs uppercase tracking-widest">Total Service Calls Processed Monthly</FormLabel>
                            <FormControl>
                                <Input type="number" placeholder="Total service calls processed monthly." className="rounded-none border-primary/30 bg-black/20 shadow-inner text-secondary-foreground placeholder:text-secondary-foreground/30 focus-visible:ring-primary focus-visible:border-primary h-12 font-mono" {...field} />
                            </FormControl>
                            <FormMessage className="font-mono text-xs text-destructive" />
                        </FormItem>} />
            </div>

            <FormField control={control} name="description" render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-primary font-mono text-xs uppercase tracking-widest">Brief Description of Lead Handling Challenges</FormLabel>
                        <FormControl>
                            <textarea placeholder="Explain any issues you face with missed leads, after-hours calls, etc." className="flex min-h-[120px] w-full rounded-none border border-primary/30 bg-black/20 px-3 py-3 text-base shadow-inner text-secondary-foreground placeholder:text-secondary-foreground/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 md:text-sm font-mono resize-y" {...field} />
                        </FormControl>
                        <FormMessage className="font-mono text-xs text-destructive" />
                    </FormItem>} />

            <FormField control={control} name="initiate_system_sync" rules={{
      required: "System sync confirmation is required"
    }} render={({
      field
    }) => <FormItem className="space-y-3">
                        <FormLabel className="text-primary font-mono text-xs uppercase tracking-widest">INITIATE SYSTEM SYNC</FormLabel>
                        <FormControl>
                            <div className="flex items-center space-x-3 p-4 border border-primary/30 bg-black/20">
                                <input type="checkbox" className="size-4 border-primary text-primary bg-transparent focus:ring-0 accent-[#B5A642] cursor-pointer" checked={field.value} onChange={field.onChange} />
                                <span className="text-secondary-foreground font-mono text-xs uppercase tracking-wider">Yes, initiate sync</span>
                            </div>
                        </FormControl>
                        <FormMessage className="font-mono text-xs text-destructive" />
                    </FormItem>} />

            {submitError && <div className="p-4 border border-destructive bg-destructive/10 text-destructive font-mono text-sm">
                    <ShieldAlert className="inline-block mr-2 size-4" />
                    {submitError}
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-auto py-6 mt-8 rounded-none bg-[#B5A642] text-primary-foreground font-serif text-xl uppercase tracking-widest shadow-[4px_4px_0px_0px_rgba(0,0,0,0.5)] active:translate-y-[2px] active:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.5)] transition-all hover:bg-[#B5A642]/90">
                {isSubmitting ? <>
                        <Loader2 className="animate-spin mr-3 size-6" />
                        Syncing Data...
                    </> : 'Initiate System Sync'}
            </Button>
        </Form>;
}
export default function OperationalAuditForm() {
  const [answers, setAnswers] = useState<Record<string, string | null>>({
    q1: null,
    q2: null,
    q3: null
  });
  const [showForm, setShowForm] = useState(false);
  const questions = [{
    id: 'q1',
    title: "01. Call Capture Integrity",
    text: "Are your clients currently losing a 30-50% of after-hours leads to competitors because of missed after-hours calls?\"",
    options: [{
      value: 'A',
      label: "No, capture is 100%"
    }, {
      value: 'B',
      label: "Yes, leakage detected"
    }]
  }, {
    id: 'q2',
    title: "02. CRM Synchronization",
    text: "Is your current customer relationship management system fully automated and integrated with field operations?",
    options: [{
      value: 'A',
      label: "Yes, fully synced"
    }, {
      value: 'B',
      label: "No, manual gaps exist"
    }]
  }, {
    id: 'q3',
    title: "03. Pipeline Visibility",
    text: "Do you have real-time, transparent tracking of every lead from initial contact to final conversion?",
    options: [{
      value: 'A',
      label: "Yes, full visibility"
    }, {
      value: 'B',
      label: "No, blind spots present"
    }]
  }];
  const handleAnswer = (qId: string, value: string) => {
    setAnswers(prev => {
      const newAnswers = {
        ...prev,
        [qId]: value
      };
      return newAnswers;
    });
  };
  useEffect(() => {
    if (answers.q1 === 'B' && answers.q2 === 'B' && answers.q3 === 'B') {
      setShowForm(true);
    } else {
      setShowForm(false);
    }
  }, [answers]);
  const progressCount = Object.values(answers).filter(v => v === 'B').length;
  const progressPercentage = progressCount / 3 * 100;
  return <section id="operational-audit-form" className="dark bg-[#001F36] text-[#F5F5DC] py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(var(--color-foreground) 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }}></div>
            <div className="absolute inset-0 pointer-events-none opacity-20 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606688/img/warehouse_texture_pattern.png)] bg-repeat animate-pulse" />
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle,rgba(181,166,66,0.05)_0%,transparent_70%)]" />
            
            <div className="max-w-4xl mx-auto relative z-10">
                
                <div className="bg-[#003153] relative shadow-2xl">
                    
                    
                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-secondary-foreground/10">
                        <div className="h-full bg-primary transition-all duration-1000 ease-out shadow-[0_0_10px_var(--color-primary)]" style={{
            width: `${progressPercentage}%`
          }} />
                    </div>

                    <div className="p-6 md:p-10 lg:p-12">
                        
                        
                        

                        
                        <div className="space-y-8">
                            {questions.map((q, i) => <div key={q.id} data-index={i} className="space-y-4">
                                    <div className="flex items-baseline justify-between border-b border-primary/20 pb-2">
                                        <h3 className="text-lg font-serif" style={{
                  color: 'oklab(0.977025 -0.002299 0.007738 / 0.8)'
                }}>{q.title}</h3>
                                        {answers[q.id] === 'B' && <CheckCircle2 className="size-5 text-primary" />}
                                    </div>
                                    <p className="font-mono text-sm" style={{
                color: 'oklab(0.977025 -0.002299 0.007738 / 0.8)'
              }}>{q.text}</p>
                                    
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                                        {q.options.map((opt, optIndex) => {
                  const isSelected = answers[q.id] === opt.value;
                  return <Button key={opt.value} data-index={optIndex} onClick={() => handleAnswer(q.id, opt.value)} variant="ghost" className={`
                                                        relative p-3 border-2 text-left h-auto transition-all duration-200 rounded-none group overflow-hidden
                                                        ${isSelected ? 'bg-[#B5A642] border-primary text-primary-foreground shadow-[4px_4px_0px_0px_rgba(0,0,0,0.5)] translate-y-[-2px]' : 'bg-background border-transparent text-foreground hover:border-primary hover:shadow-[4px_4px_0px_0px_var(--color-primary)] hover:translate-y-[-2px]'}
                                                    `}>
                                                    
                                                    <div className={`absolute inset-0 bg-primary/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out ${isSelected ? 'hidden' : ''}`} />
                                                    
                                                    <div className="relative z-10 flex items-center justify-between w-full">
                                                        <div>
                                                            <span className="block text-2xl font-serif mb-1">{opt.value}</span>
                                                            <span className="block text-xs font-mono uppercase tracking-wider opacity-90">{opt.label}</span>
                                                        </div>
                                                        <ChevronRight className={`size-6 transition-transform ${isSelected ? 'translate-x-1' : 'opacity-0 group-hover:opacity-100 group-hover:translate-x-1'}`} />
                                                    </div>
                                                </Button>;
                })}
                                    </div>
                                </div>)}
                        </div>

                        
                        <div className={`grid transition-[grid-template-rows,margin,opacity] duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${showForm ? 'grid-rows-[1fr] mt-16 opacity-100' : 'grid-rows-[0fr] mt-0 opacity-0'}`}>
                            <div className="overflow-hidden">
                                <div className="border-t-2 border-primary pt-12 relative">
                                    
                                    <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-primary -translate-x-[2px] -translate-y-[2px]" />
                                    <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-primary translate-x-[2px] -translate-y-[2px]" />
                                    
                                    <div className="mb-8">
                                        <h3 className="text-2xl font-serif text-secondary-foreground uppercase tracking-widest mb-2 flex items-center gap-3">
                                            <span className="w-2 h-2 bg-primary animate-pulse" />
                                            Data Capture Required
                                        </h3>
                                        <p className="text-[rgb(217,119,6)] font-mono text-sm">
                                            Vulnerabilities detected. Please provide operational details to initiate system sync and receive your custom audit report.
                                        </p>
                                    </div>

                                    <FormProvider formId="operational_audit_form_operationalaudit" sectionName="Operational Audit Form" defaultValues={{
                  company_name: "",
                  contact_name: "",
                  email: "",
                  phone: "",
                  crm_system: "",
                  monthly_call_volume: "",
                  description: "",
                  initiate_system_sync: false
                }} validationRules={{
                  company_name: {
                    required: true
                  },
                  contact_name: {
                    required: true
                  },
                  email: {
                    required: true,
                    pattern: "email"
                  },
                  phone: {
                    required: true,
                    pattern: "phone"
                  },
                  monthly_call_volume: {
                    min: 0
                  },
                  initiate_system_sync: {
                    required: true
                  }
                }} submitText="Initiate System Sync" successMessage="Data received. Our analysts are compiling your operational audit. You will be contacted shortly.">
                                        <AuditFormContent />
                                    </FormProvider>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </section>;
}